/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { z } from 'zod';
import { DEFAULT_LEVERED_FINANCING_INPUTS } from './defaults';
const percentageSchema = (defaultValue) => z.number().min(0).max(1).default(defaultValue);
const positiveIntegerSchema = (defaultValue) => z.int().positive().default(defaultValue);
// Lenders will not size below 1.30x (Finance, Oct 13).
const MINIMUM_DSCR = 1.3;
// Facility base rates reflect current market rates: required, no default, and may be negative.
const baseRateSchema = z.number().min(-1).max(1);
const optionalDateSchema = z.string().nullable().default(null);
export const termDebtInputsSchema = z.strictObject({
    contracted_dscr: z
        .number()
        .min(MINIMUM_DSCR)
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.contracted_dscr),
    merchant_dscr: z
        .number()
        .min(MINIMUM_DSCR)
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.merchant_dscr),
    // Omitted (null) derives the tenor from the longest asset debt-sizing window.
    tenor_years: z.int().positive().nullable().default(null),
    pv_merchant_sizing_years: z
        .int()
        .min(0)
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.pv_merchant_sizing_years),
    bess_merchant_sizing_years: z
        .int()
        .min(0)
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.bess_merchant_sizing_years),
    network_upgrade_reimbursements_in_cfads: z
        .boolean()
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.network_upgrade_reimbursements_in_cfads),
    // Required with no default so each run records the current market rate.
    swap_rate: z.number().min(0).max(1),
    percent_swapped: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.percent_swapped),
    floating_base_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.floating_base_rate),
    base_margin: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.base_margin),
    margin_step_up_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.margin_step_up_rate),
    margin_step_up_frequency_years: positiveIntegerSchema(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.margin_step_up_frequency_years),
    refinancing_fee_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.refinancing_fee_rate),
    refinancing_frequency_years: positiveIntegerSchema(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.refinancing_frequency_years),
    refinancing_fee_amortization_years: positiveIntegerSchema(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.refinancing_fee_amortization_years),
    administration_fee_annual: z
        .number()
        .min(0)
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.administration_fee_annual),
    upfront_fee_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.upfront_fee_rate),
    debt_service_reserve_lc_fee_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.debt_service_reserve_lc_fee_rate),
    debt_service_reserve_months: positiveIntegerSchema(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.debt_service_reserve_months),
    day_count_basis: z
        .union([z.literal(360), z.literal(365)])
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.term_debt.day_count_basis),
});
const floatingFacilityInputsSchema = z.strictObject({
    base_rate: baseRateSchema,
    margin: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.construction.construction_facility.margin),
});
const constructionFacilityInputsSchema = floatingFacilityInputsSchema.extend({
    commitment_fee_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.construction.construction_facility.commitment_fee_rate),
});
const taxEquityBridgeInputsSchema = constructionFacilityInputsSchema.extend({
    upfront_fee_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.construction.tax_equity_bridge.upfront_fee_rate),
    // Before signing, the bridge lends against ITC value at the credit transfer price; from
    // signing, it lends against the signed tax-equity commitment.
    tax_equity_committed: z
        .boolean()
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.construction.tax_equity_bridge.tax_equity_committed),
    commitment_date: optionalDateSchema,
    uncommitted_advance_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.construction.tax_equity_bridge.uncommitted_advance_rate),
    committed_advance_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.construction.tax_equity_bridge.committed_advance_rate),
    credit_transfer_price: z
        .number()
        .gt(0)
        .max(1)
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.construction.tax_equity_bridge.credit_transfer_price),
});
export const constructionFinancingInputsSchema = z.strictObject({
    minimum_sponsor_equity_percentage: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.construction.minimum_sponsor_equity_percentage),
    construction_security_upfront_fee_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.construction.construction_security_upfront_fee_rate),
    operational_security_upfront_fee_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.construction.operational_security_upfront_fee_rate),
    sponsor_equity_return_date: optionalDateSchema,
    construction_facility: constructionFacilityInputsSchema,
    tax_equity_bridge: taxEquityBridgeInputsSchema,
    equipment_facility: floatingFacilityInputsSchema.extend({
        margin: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.construction.equipment_facility.margin),
    }),
});
export const taxEquityInputsSchema = z.strictObject({
    hurdle_rate: z
        .number()
        .gt(0)
        .max(1)
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.hurdle_rate),
    tax_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.tax_rate),
    upfront_fee_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.upfront_fee_rate),
    early_funding_percentage: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.early_funding_percentage),
    early_funding_months_before_cod: z
        .int()
        .min(0)
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.early_funding_months_before_cod),
    flip_years: positiveIntegerSchema(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.flip_years),
    income_allocation_pre_flip: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.income_allocation_pre_flip),
    income_allocation_post_flip: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.income_allocation_post_flip),
    cash_allocation_pre_flip: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.cash_allocation_pre_flip),
    cash_allocation_post_flip: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.cash_allocation_post_flip),
    paygo_rate: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.paygo_rate),
    paygo_accrual_months: z
        .int()
        .min(0)
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.paygo_accrual_months),
    paygo_payment_month: z
        .int()
        .min(1)
        .max(12)
        .multipleOf(3)
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.paygo_payment_month),
    paygo_cfads_credit_percentage: percentageSchema(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.paygo_cfads_credit_percentage),
    network_upgrade_reimbursements_to_sponsor: z
        .boolean()
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.network_upgrade_reimbursements_to_sponsor),
    partnership_year_convention: z
        .enum(['calendar', 'cod_anniversary'])
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity.partnership_year_convention),
});
// Required per asset the project has; never defaulted by the backend.
const taxCreditElectionSchema = z.enum(['none', 'itc', 'ptc']).nullable().default(null);
export const taxCreditElectionInputsSchema = z.strictObject({
    pv: taxCreditElectionSchema,
    bess: taxCreditElectionSchema,
});
export const leveredSolverInputsSchema = z.strictObject({
    irr_tolerance: z
        .number()
        .positive()
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.solver.irr_tolerance),
    cash_tolerance: z
        .number()
        .positive()
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.solver.cash_tolerance),
    price_tolerance: z
        .number()
        .positive()
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.solver.price_tolerance),
    construction_circularity_tolerance: z
        .number()
        .positive()
        .default(DEFAULT_LEVERED_FINANCING_INPUTS.solver.construction_circularity_tolerance),
    maximum_iterations: positiveIntegerSchema(DEFAULT_LEVERED_FINANCING_INPUTS.solver.maximum_iterations),
});
// `term_debt` and `construction` are required because the swap rate and the facility
// base rates have no default; the credit elections come from the project's tax credits.
export const leveredFinancingInputsSchema = z.strictObject({
    term_debt: termDebtInputsSchema,
    construction: constructionFinancingInputsSchema,
    tax_equity: taxEquityInputsSchema.default(DEFAULT_LEVERED_FINANCING_INPUTS.tax_equity),
    solver: leveredSolverInputsSchema.default(DEFAULT_LEVERED_FINANCING_INPUTS.solver),
    tax_credit_elections: taxCreditElectionInputsSchema,
});
// The schemas read their defaults from this prefill, so it is checked against the schema
// types here rather than where it is declared, which would make the types circular.
DEFAULT_LEVERED_FINANCING_INPUTS;
//# sourceMappingURL=schemas.js.map